import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronRight, Copy, Download, ExternalLink, Share2, Smartphone, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inAppBrowser, isStandalone, useInstallPrompt } from "@/lib/pwa";

const STEPS = [
  { t: "Buka tautan ini di Chrome", d: "Pastikan bukan browser dalam WhatsApp/Facebook. Kalau terbuka di sana, ketuk ⋮ → \"Buka di Chrome\"." },
  { t: "Ketuk menu ⋮ di kanan atas Chrome", d: "Ikon tiga titik, sebaris dengan alamat situs." },
  { t: "Pilih \"Instal aplikasi\" atau \"Tambahkan ke layar Utama\"", d: "Kedua pilihan sama-sama memasang ALPHA BILL sebagai aplikasi." },
  { t: "Ketuk \"Instal\" pada kotak konfirmasi", d: "Ikon ALPHA BILL akan muncul di layar utama HP Anda." },
  { t: "Buka dari ikon ALPHA BILL, lalu izinkan notifikasi", d: "Tekan \"Aktifkan Sekarang\" pada banner kuning agar alarm saldo berbunyi." },
];

/** Panduan instal PWA — tidak bergantung hanya pada event Chrome. */
export default function InstallGuide() {
  const { prompt, installed, install } = useInstallPrompt();
  const [ready, setReady] = useState(!!prompt);
  const app = inAppBrowser();
  const url = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => setReady(!!prompt), [prompt]);
  useEffect(() => {
    /* Chrome kadang memicu kesiapan instal beberapa detik setelah ada interaksi. */
    const id = setInterval(() => setReady(!!window.__abInstallPrompt), 1000);
    setTimeout(() => clearInterval(id), 60000);
    return () => clearInterval(id);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tautan disalin", { description: "Tempel di Chrome untuk memasang aplikasi." });
    } catch {
      toast.info("Salin manual", { description: url });
    }
  };

  const doInstall = async () => {
    const ok = await install();
    if (!ok && !window.__abInstallPrompt) {
      toast.info("Ikuti langkah di bawah", { description: "Chrome belum menawarkan instal otomatis di HP ini." });
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10" data-testid="install-guide">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali
        </Link>

        <div className="flex items-center gap-3">
          <div className="rounded-2xl border border-violet-500/40 bg-violet-500/10 p-3">
            <Smartphone className="h-6 w-6 text-violet-400" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold sm:text-4xl">Pasang ALPHA BILL di HP</h1>
            <p className="text-sm text-muted-foreground">Sekali pasang, buka seperti aplikasi biasa — lengkap dengan alarm saldo.</p>
          </div>
        </div>

        {installed || isStandalone() ? (
          <div className="mt-10 flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5" data-testid="already-installed">
            <Check className="h-5 w-5 text-emerald-400" />
            <p className="text-sm text-emerald-300">Aplikasi sudah terpasang di perangkat ini. Buka dari ikon ALPHA BILL di layar utama.</p>
          </div>
        ) : (
          <>
            {app && (
              <div className="mt-10 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5" data-testid="inapp-warning">
                <div className="flex items-start gap-3">
                  <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">Anda membuka dari browser dalam {app}</p>
                    <p className="text-xs text-muted-foreground">Aplikasi tidak bisa dipasang dari sini. Salin tautan lalu buka di Chrome.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={copy} className="rounded-full bg-amber-500 text-black hover:bg-amber-400" data-testid="copy-link">
                        <Copy className="mr-1 h-3.5 w-3.5" /> Salin Tautan
                      </Button>
                      <Button asChild size="sm" variant="outline" className="rounded-full">
                        <a href={url} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Coba Buka di Chrome</a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button
                size="lg" onClick={doInstall} disabled={!ready} data-testid="guide-install"
                className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
              >
                <Download className="mr-2 h-4 w-4" /> {ready ? "Instal Sekarang (1 klik)" : "Menunggu Chrome siap…"}
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="share-guide">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Pasang aplikasi ALPHA BILL di HP: buka ${url}/instal di Chrome, lalu ikuti langkahnya.`)}`}
                  target="_blank" rel="noreferrer"
                >
                  <Share2 className="mr-2 h-4 w-4" /> Bagikan Panduan (WhatsApp)
                </a>
              </Button>
            </div>
            {!ready && (
              <p className="mt-3 text-xs text-muted-foreground" data-testid="not-ready-note">
                Chrome belum menawarkan instal otomatis (biasanya karena aplikasi sudah pernah dipasang, atau perlu beberapa detik interaksi).
                Gunakan langkah manual di bawah — hasilnya sama.
              </p>
            )}

            <div className="mt-12 space-y-4">
              <h2 className="text-base font-semibold md:text-lg">Langkah manual (Chrome Android)</h2>
              {STEPS.map((s, i) => (
                <div key={s.t} className="flex items-start gap-4 rounded-2xl border border-border bg-card/60 p-4 transition-colors duration-200 hover:border-violet-500/40">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-500/15 font-mono text-sm font-bold text-violet-300">{i + 1}</span>
                  <div>
                    <p className="text-sm font-semibold">{s.t}</p>
                    <p className="text-xs text-muted-foreground">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-border bg-secondary/30 p-5 text-xs text-muted-foreground">
              <p className="mb-2 font-semibold text-foreground">Kalau menu "Instal aplikasi" tidak muncul di Chrome</p>
              <p className="flex items-start gap-2"><ChevronRight className="mt-0.5 h-3 w-3 shrink-0" /> Aplikasi mungkin sudah terpasang — cek layar utama, atau menu ⋮ akan menampilkan "Buka aplikasi".</p>
              <p className="flex items-start gap-2"><ChevronRight className="mt-0.5 h-3 w-3 shrink-0" /> Pakai pilihan "Tambahkan ke layar Utama"; hasilnya sama dengan instal.</p>
              <p className="flex items-start gap-2"><ChevronRight className="mt-0.5 h-3 w-3 shrink-0" /> Pastikan Chrome versi terbaru dan mode Penjelajahan Rahasia tidak aktif.</p>
              <p className="flex items-start gap-2"><ChevronRight className="mt-0.5 h-3 w-3 shrink-0" /> Muat ulang halaman sekali, tunggu 10 detik, lalu buka menu ⋮ kembali.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
